import React from "react";
import {
  Building2,
  CalendarDays,
  Car,
  ChevronDown,
  CircleAlert,
  Coffee,
  DoorOpen,
  Eye,
  Filter,
  HeartPulse,
  Home,
  LogOut,
  Menu,
  Pencil,
  Package,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getInstitutionInitials } from "../../utils/institutionBranding";
import hostelService from "../../services/hostelService";
import { academicCalendarService } from "../../services/academicCalendarService";
import TransportWorkspace, {
  transportSections,
} from "../transport/TransportWorkspace";
import EstatesWorkspace, { estatesSections } from "../estates/EstatesWorkspace";
import ResourceServiceBooking from "../resource-service-booking";
import EstatesServiceRequests from "../estates/EstatesServiceRequests";

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
const defaultWorkspaceForRoles = (roles = []) => {
  const preferredWorkspaces = [
    ["estates_officer", "estates"],
    ["transport_officer", "transport"],
    ["hostel_officer", "hostel"],
    ["medical_officer", "medical"],
    ["catering_officer", "catering"],
    ["stores_officer", "stores"],
    ["ict_officer", "ict"],
    ["corporate_officer", "corporate"],
  ];
  return (
    preferredWorkspaces.find(([role]) => roles.includes(role))?.[1] || "hostel"
  );
};
const labelRole = (value) =>
  String(value || "operations staff")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
const statusTone = (status) =>
  ({
    AVAILABLE: "bg-emerald-50 text-emerald-700 border-emerald-200",
    HELD_RESERVED: "bg-amber-50 text-amber-700 border-amber-200",
    PENDING_PAYMENT: "bg-amber-50 text-amber-700 border-amber-200",
    ALLOCATED: "bg-blue-50 text-blue-700 border-blue-200",
    OCCUPIED: "bg-indigo-50 text-indigo-700 border-indigo-200",
    MAINTENANCE: "bg-slate-100 text-slate-600 border-slate-200",
    OUT_OF_SERVICE: "bg-rose-50 text-rose-700 border-rose-200",
  })[status] || "bg-slate-50 text-slate-600 border-slate-200";
const activeHostelReservationStatuses = new Set([
  "HELD_RESERVED",
  "PENDING_PAYMENT",
  "ALLOCATED",
  "OCCUPIED",
]);
const blockInventoryCounts = (block, reservations = []) => {
  const reservationByBed = new Map();
  reservations
    .filter((reservation) => reservation?.bed_id)
    .forEach((reservation) => {
      const status = String(reservation.status || "").toUpperCase();
      const key = String(reservation.bed_id);
      const existing = reservationByBed.get(key);
      if (
        !existing ||
        (!activeHostelReservationStatuses.has(existing) &&
          activeHostelReservationStatuses.has(status))
      ) {
        reservationByBed.set(key, status);
      }
    });

  return (block.rooms || [])
    .flatMap((room) => room.beds || [])
    .reduce(
      (counts, bed) => {
        const bedStatus = String(bed.status || "").toUpperCase();
        const reservationStatus = reservationByBed.get(String(bed.id));
        const status = reservationStatus || bedStatus;

        if (["ALLOCATED", "OCCUPIED"].includes(status)) {
          counts.allocated += 1;
        } else if (["HELD_RESERVED", "PENDING_PAYMENT"].includes(status)) {
          counts.held += 1;
        } else if (
          ["AVAILABLE", "CANCELLED", "EXPIRED", "RELEASED"].includes(status)
        ) {
          counts.available += 1;
        }

        return counts;
      },
      { available: 0, held: 0, allocated: 0 },
    );
};
const effectiveBedStatus = (bed, reservations = []) => {
  const reservation = reservations.find(
    (item) =>
      String(item?.bed_id || "") === String(bed?.id || "") &&
      activeHostelReservationStatuses.has(
        String(item?.status || "").toUpperCase(),
      ),
  );
  return reservation
    ? String(reservation.status).toUpperCase()
    : String(bed?.status || "AVAILABLE").toUpperCase();
};
const money = (value) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
const dateTime = (value) =>
  value
    ? new Intl.DateTimeFormat("en-KE", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";

const workspaces = [
  {
    id: "hostel",
    label: "Hostel ORRA",
    icon: Home,
    roles: [
      "hostel_officer",
      "dean_of_students",
      "system_administrator",
      "deputy_principal_administration",
    ],
  },
  {
    id: "transport",
    label: "Transport",
    icon: Car,
    roles: [
      "transport_officer",
      "system_administrator",
      "deputy_principal_administration",
      "dean_of_students",
      "hostel_officer",
      "medical_officer",
      "catering_officer",
      "stores_officer",
      "ict_officer",
      "corporate_officer",
      "procurement_officer",
      "chief_principal",
      "deputy_principal_academic",
      "registrar_academic",
      "dean_of_faculty",
      "head_of_department",
      "head_of_section",
      "poe_manager",
      "timetable_master",
      "assessment_officer",
      "quality_assurance_officer",
      "finance_officer",
      "trainer_lecturer",
    ],
  },
  {
    id: "estates",
    label: "Estates",
    icon: Building2,
    roles: [
      "estates_officer",
      "system_administrator",
      "deputy_principal_administration",
    ],
  },
  {
    id: "medical",
    label: "Medical",
    icon: HeartPulse,
    roles: [
      "medical_officer",
      "system_administrator",
      "deputy_principal_administration",
    ],
  },
  {
    id: "catering",
    label: "Catering",
    icon: Coffee,
    roles: [
      "catering_officer",
      "system_administrator",
      "deputy_principal_administration",
    ],
  },
  {
    id: "stores",
    label: "Stores",
    icon: Package,
    roles: [
      "stores_officer",
      "procurement_officer",
      "system_administrator",
      "deputy_principal_administration",
    ],
  },
  {
    id: "ict",
    label: "ICT",
    icon: Building2,
    roles: ["ict_officer", "system_administrator", "deputy_principal_administration"],
  },
  {
    id: "corporate",
    label: "Corporate affairs",
    icon: Users,
    roles: ["corporate_officer", "system_administrator", "deputy_principal_administration"],
  },
];

const hostelSections = [
  { id: "inventory", label: "Hostel" },
  { id: "amenities", label: "Amenities" },
  { id: "reservations", label: "Reservations" },
  { id: "requests", label: "Requests" },
];

const Metric = ({ label, value, tone = "text-slate-900", icon: Icon }) => (
  <div className="rounded-xl border bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <Icon className={`h-5 w-5 ${tone}`} />
    </div>
    <p className={`mt-2 text-2xl font-bold ${tone}`}>{value}</p>
  </div>
);

const FormField = ({ label, children, className = "" }) => (
  <label className={`flex min-w-0 flex-col gap-1 ${className}`}>
    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
      {label}
    </span>
    {children}
  </label>
);

const HostelWorkspace = ({ notify, view = "overview" }) => {
  const [dashboard, setDashboard] = React.useState({
    metrics: {},
    blocks: [],
    reservations: [],
    excess_credits: [],
  });
  const [bookingPeriods, setBookingPeriods] = React.useState([]);
  const [bookingPeriod, setBookingPeriod] = React.useState({
    academicYearId: "",
    semesterId: "",
  });
  const [bookingWindow, setBookingWindow] = React.useState({
    configured: false,
    booking_enabled: false,
    opens_on: "",
    closes_on: "",
    status: "unconfigured",
    can_book: false,
  });
  const [bookingWindowSaving, setBookingWindowSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [showInventoryForm, setShowInventoryForm] = React.useState(false);
  const [amenities, setAmenities] = React.useState([]);
  const [amenityName, setAmenityName] = React.useState("");
  const [editingAmenity, setEditingAmenity] = React.useState(null);
  const [editingBlock, setEditingBlock] = React.useState(null);
  const [viewingBlock, setViewingBlock] = React.useState(null);
  const [inventoryQuery, setInventoryQuery] = React.useState("");
  const [inventoryCategory, setInventoryCategory] = React.useState("all");
  const [inventoryStatus, setInventoryStatus] = React.useState("all");
  const [blockForm, setBlockForm] = React.useState({
    name: "",
    category: "standard",
    gender: "any",
    roomCount: "",
    bedsPerRoom: "",
    bedRate: "",
    cautionDeposit: "",
    utilityFee: "",
    amenityIds: [],
    studentBookingEnabled: true,
  });
  const [roomForms, setRoomForms] = React.useState({});
  const [bedForms, setBedForms] = React.useState({});
  const [showRoomBedEditor, setShowRoomBedEditor] = React.useState(false);
  const [form, setForm] = React.useState({
    name: "",
    category: "standard",
    gender: "any",
    roomCount: "",
    bedsPerRoom: "",
    amenityIds: [],
    studentBookingEnabled: true,
    bedRate: "",
    cautionDeposit: "",
    utilityFee: "",
  });

  const loadBookingSettings = React.useCallback(async () => {
    const years = await academicCalendarService.getAcademicYears();
    const periodGroups = await Promise.all(
      (years || []).map(async (year) => ({
        year,
        semesters: await academicCalendarService.getSemesters(year.id),
      })),
    );
    const periods = periodGroups.flatMap(({ year, semesters }) =>
      (semesters || []).map((semester) => ({ year, semester })),
    );
    setBookingPeriods(periods);
    const preferred =
      periods.find(
        (period) =>
          String(period.semester.status || "").toLowerCase() === "active" &&
          String(period.year.status || "").toLowerCase() === "active",
      ) || periods[0];
    if (!preferred) {
      setBookingPeriod({ academicYearId: "", semesterId: "" });
      setBookingWindow({
        configured: false,
        booking_enabled: false,
        opens_on: "",
        closes_on: "",
        status: "unconfigured",
        can_book: false,
      });
      return;
    }
    setBookingPeriod({
      academicYearId: preferred.year.id,
      semesterId: preferred.semester.id,
    });
    setBookingWindow(
      (await hostelService.getBookingWindow(
        preferred.year.id,
        preferred.semester.id,
      )) || {},
    );
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [nextDashboard, nextAmenities, excessCredits] = await Promise.all([
        hostelService.getStaffDashboard(),
        hostelService.getAmenities(),
        hostelService.getStaffExcessCredits(),
      ]);
      setDashboard({
        ...nextDashboard,
        excess_credits: excessCredits || [],
      });
      setAmenities(nextAmenities);
      await loadBookingSettings();
    } catch (error) {
      notify(error?.message || "Hostel data could not be loaded", "error");
    } finally {
      setLoading(false);
    }
  }, [loadBookingSettings, notify]);
  React.useEffect(() => {
    load();
  }, [load]);

  const selectBookingPeriod = async (academicYearId, semesterId) => {
    setBookingPeriod({ academicYearId, semesterId });
    if (!academicYearId || !semesterId) return;
    try {
      setBookingWindow(
        (await hostelService.getBookingWindow(academicYearId, semesterId)) ||
          {},
      );
    } catch (error) {
      notify(error?.message || "Booking dates could not be loaded", "error");
    }
  };
  const saveBookingWindow = async (event) => {
    event.preventDefault();
    if (!bookingPeriod.academicYearId || !bookingPeriod.semesterId) return;
    setBookingWindowSaving(true);
    try {
      const saved = await hostelService.saveBookingWindow({
        academicYearId: bookingPeriod.academicYearId,
        semesterId: bookingPeriod.semesterId,
        bookingEnabled: bookingWindow.booking_enabled,
        opensOn: bookingWindow.opens_on,
        closesOn: bookingWindow.closes_on,
      });
      setBookingWindow(saved || bookingWindow);
      notify("Hostel booking window saved");
    } catch (error) {
      notify(
        error?.message || "Hostel booking window could not be saved",
        "error",
      );
    } finally {
      setBookingWindowSaving(false);
    }
  };

  const run = async (key, action, message) => {
    setBusy(key);
    try {
      await action();
      notify(message);
      await load();
    } catch (error) {
      notify(error?.message || "The operation could not be completed", "error");
    } finally {
      setBusy("");
    }
  };
  const submitInventory = async (event) => {
    event.preventDefault();
    return run(
      "inventory",
      () => hostelService.createHostelInventory(form),
      "Hostel, rooms and beds created",
    );
  };
  const openBlockEditor = (block) => {
    const selectedNames = new Set(
      (block.amenities || []).map((name) => String(name).trim().toLowerCase()),
    );
    const roomCapacities = Array.from(
      new Set((block.rooms || []).map((room) => Number(room.capacity || 0))),
    ).filter(Boolean);
    setEditingBlock(block);
    setShowRoomBedEditor(false);
    setBlockForm({
      name: block.name || "",
      category: block.hostel_category || "standard",
      gender: block.gender_eligibility || "any",
      roomCount: block.rooms?.length || "",
      bedsPerRoom: roomCapacities.length === 1 ? roomCapacities[0] : "",
      bedRate: block.bed_rate ?? block.room_rate ?? "",
      cautionDeposit: block.caution_deposit ?? "",
      utilityFee: block.utility_fee ?? "",
      amenityIds: amenities
        .filter((amenity) =>
          selectedNames.has(String(amenity.name).trim().toLowerCase()),
        )
        .map((amenity) => amenity.id),
      studentBookingEnabled:
        block.hostel_category === "staff"
          ? false
          : block.student_booking_enabled !== false,
    });
    setRoomForms(
      Object.fromEntries(
        (block.rooms || []).map((room) => [
          room.id,
          {
            roomNumber: room.room_number || "",
            roomType: room.room_type || "shared",
            capacity: room.capacity || room.beds?.length || 1,
            gender: room.gender_eligibility || "any",
          },
        ]),
      ),
    );
    setBedForms(
      Object.fromEntries(
        (block.rooms || []).flatMap((room) =>
          (room.beds || []).map((bed) => [
            bed.id,
            {
              bedNumber: bed.bed_number || "",
              accessible: Boolean(bed.special_needs_accessible),
              notes: bed.notes || "",
              status: bed.status || "AVAILABLE",
            },
          ]),
        ),
      ),
    );
  };
  const submitBlockEdit = async (event) => {
    event.preventDefault();
    if (!editingBlock) return;
    const roomCount = Number(blockForm.roomCount);
    const bedsPerRoomValue = String(blockForm.bedsPerRoom ?? "").trim();
    const bedsPerRoom = Number(blockForm.bedsPerRoom);
    const roomCapacities = Array.from(
      new Set(
        (editingBlock.rooms || []).map((room) => Number(room.capacity || 0)),
      ),
    ).filter(Boolean);
    const inventoryChanged =
      roomCount !== (editingBlock.rooms || []).length ||
      (bedsPerRoomValue !== "" &&
        (roomCapacities.length !== 1 || bedsPerRoom !== roomCapacities[0]));
    if (
      !Number.isInteger(roomCount) ||
      roomCount < 1 ||
      roomCount > 500 ||
      (inventoryChanged &&
        (!Number.isInteger(bedsPerRoom) ||
          bedsPerRoom < 1 ||
          bedsPerRoom > 100))
    ) {
      notify(
        "Enter a valid room count (1–500) and beds per room (1–100)",
        "error",
      );
      return;
    }
    const key = `edit-block-${editingBlock.id}`;
    setBusy(key);
    try {
      await hostelService.updateHostelBlock({
        blockId: editingBlock.id,
        ...blockForm,
      });
      if (inventoryChanged) {
        await hostelService.resizeHostelInventory({
          blockId: editingBlock.id,
          roomCount,
          bedsPerRoom,
        });
      }
      setEditingBlock(null);
      notify("Hostel details updated");
      await load();
    } catch (error) {
      notify(error?.message || "Hostel could not be updated", "error");
    } finally {
      setBusy("");
    }
  };
  const saveRoomEdit = async (room) => {
    const values = roomForms[room.id];
    if (!values) return;
    const key = `room-${room.id}`;
    setBusy(key);
    try {
      const updated = await hostelService.updateRoom({
        roomId: room.id,
        ...values,
      });
      setEditingBlock((current) =>
        current
          ? {
              ...current,
              rooms: (current.rooms || []).map((item) =>
                item.id === room.id ? { ...item, ...updated } : item,
              ),
            }
          : current,
      );
      notify(`Room ${values.roomNumber} updated`);
      await load();
    } catch (error) {
      notify(error?.message || "Room could not be updated", "error");
    } finally {
      setBusy("");
    }
  };
  const saveBedEdit = async (bed, roomId) => {
    const values = bedForms[bed.id];
    if (!values) return;
    const key = `bed-${bed.id}`;
    setBusy(key);
    try {
      const updated = await hostelService.updateBed({
        bedId: bed.id,
        ...values,
      });
      setEditingBlock((current) =>
        current
          ? {
              ...current,
              rooms: (current.rooms || []).map((room) =>
                room.id === roomId
                  ? {
                      ...room,
                      beds: (room.beds || []).map((item) =>
                        item.id === bed.id ? { ...item, ...updated } : item,
                      ),
                    }
                  : room,
              ),
            }
          : current,
      );
      notify(`Bed ${values.bedNumber} updated`);
      await load();
    } catch (error) {
      notify(error?.message || "Bed could not be updated", "error");
    } finally {
      setBusy("");
    }
  };
  const removeBlock = async (block) => {
    const confirmed = window.confirm(
      `Delete ${block.name}? Hostels with reservation history will be archived instead so those records remain available.`,
    );
    if (!confirmed) return;
    const key = `delete-block-${block.id}`;
    setBusy(key);
    try {
      const result = await hostelService.deleteHostelBlock(block.id);
      if (viewingBlock?.id === block.id) setViewingBlock(null);
      notify(
        result?.archived
          ? "Hostel archived because it has reservation history"
          : "Hostel deleted",
      );
      await load();
    } catch (error) {
      notify(error?.message || "Hostel could not be deleted", "error");
    } finally {
      setBusy("");
    }
  };
  const saveAmenity = async (event) => {
    event.preventDefault();
    const name = amenityName.trim();
    if (!name) return;
    setBusy("amenity");
    try {
      if (editingAmenity) {
        await hostelService.updateAmenity(editingAmenity.id, name);
      } else {
        await hostelService.createAmenity(name);
      }
      setAmenityName("");
      setEditingAmenity(null);
      setAmenities(await hostelService.getAmenities());
      notify(editingAmenity ? "Amenity updated" : "Amenity added");
    } catch (error) {
      notify(error?.message || "Amenity could not be saved", "error");
    } finally {
      setBusy("");
    }
  };
  const removeAmenity = async (amenity) => {
    setBusy(`amenity-${amenity.id}`);
    try {
      await hostelService.deleteAmenity(amenity.id);
      setForm((current) => ({
        ...current,
        amenityIds: current.amenityIds.filter((id) => id !== amenity.id),
      }));
      setAmenities(await hostelService.getAmenities());
      notify("Amenity removed");
    } catch (error) {
      notify(error?.message || "Amenity could not be removed", "error");
    } finally {
      setBusy("");
    }
  };
  const restoreAmenity = async (amenity) => {
    setBusy(`amenity-${amenity.id}`);
    try {
      await hostelService.createAmenity(amenity.name);
      setAmenities(await hostelService.getAmenities());
      notify("Amenity restored");
    } catch (error) {
      notify(error?.message || "Amenity could not be restored", "error");
    } finally {
      setBusy("");
    }
  };
  const blockOptions = dashboard.blocks || [];
  const selectedCategory = form.category;
  const studentBookingForcedOff = selectedCategory === "staff";
  const inventoryRows = React.useMemo(() => {
    const query = inventoryQuery.trim().toLowerCase();
    return blockOptions
      .map((block) => ({
        block,
        counts: blockInventoryCounts(block, dashboard.reservations),
      }))
      .filter(({ block, counts }) => {
        const searchable = [
          block.name,
          block.code,
          block.hostel_category,
          block.gender_eligibility,
          ...(block.amenities || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (query && !searchable.includes(query)) return false;
        if (
          inventoryCategory !== "all" &&
          String(block.hostel_category || "standard").toLowerCase() !==
            inventoryCategory
        ) {
          return false;
        }
        if (inventoryStatus === "available" && counts.available === 0)
          return false;
        if (inventoryStatus === "held" && counts.held === 0) return false;
        if (inventoryStatus === "allocated" && counts.allocated === 0)
          return false;
        return true;
      });
  }, [
    blockOptions,
    dashboard.reservations,
    inventoryCategory,
    inventoryQuery,
    inventoryStatus,
  ]);
  const inventorySummary = React.useMemo(() => {
    const summary = {
      blocks: blockOptions.length,
      rooms: 0,
      beds: 0,
      available: 0,
      held: 0,
      allocated: 0,
    };
    blockOptions.forEach((block) => {
      summary.rooms += (block.rooms || []).length;
      summary.beds += (block.rooms || []).reduce(
        (total, room) => total + (room.beds || []).length,
        0,
      );
      const counts = blockInventoryCounts(block, dashboard.reservations);
      summary.available += counts.available;
      summary.held += counts.held;
      summary.allocated += counts.allocated;
    });
    return summary;
  }, [blockOptions, dashboard.reservations]);
  const occupancyRate = inventorySummary.beds
    ? Math.round((inventorySummary.allocated / inventorySummary.beds) * 100)
    : 0;

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="mr-auto text-sm text-slate-500">
          {view === "overview"
            ? "Accommodation capacity, occupancy and reservation activity."
            : view === "inventory"
              ? "Manage hostels, rooms and beds."
              : view === "amenities"
                ? "Manage the amenity catalogue used by hostels."
                : "Review accommodation activity and requests."}
        </p>
        <div className="flex gap-2">
          {view === "inventory" && (
            <button
              onClick={() => setShowInventoryForm((value) => !value)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              <Plus className="h-4 w-4" /> Create hostel
            </button>
          )}
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold text-slate-700"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />{" "}
            Refresh
          </button>
        </div>
      </div>
      {view === "overview" && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <Metric
              label="Active blocks"
              value={inventorySummary.blocks}
              icon={Building2}
              tone="text-blue-700"
            />
            <Metric
              label="Rooms"
              value={inventorySummary.rooms}
              icon={DoorOpen}
              tone="text-indigo-700"
            />
            <Metric
              label="Available beds"
              value={inventorySummary.available}
              icon={Home}
              tone="text-emerald-700"
            />
            <Metric
              label="Held / payment"
              value={inventorySummary.held}
              icon={RefreshCw}
              tone="text-amber-700"
            />
            <Metric
              label="Allocated"
              value={inventorySummary.allocated}
              icon={ShieldCheck}
              tone="text-sky-700"
            />
            <Metric
              label="Payment review"
              value={dashboard.metrics?.payment_review || 0}
              icon={CircleAlert}
              tone="text-rose-700"
            />
          </div>
          <form
            onSubmit={saveBookingWindow}
            className="rounded-xl border border-blue-100 bg-blue-50/50 p-5 shadow-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold">Hostel booking window</h3>
                <p className="text-sm text-slate-600">
                  Students can reserve beds only between these inclusive dates.
                </p>
              </div>
              <button
                type="submit"
                disabled={
                  bookingWindowSaving ||
                  !bookingPeriod.academicYearId ||
                  !bookingPeriod.semesterId
                }
                className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                {bookingWindowSaving ? "Saving…" : "Save booking dates"}
              </button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Academic year
                </span>
                <select
                  value={bookingPeriod.academicYearId}
                  onChange={(event) => {
                    const yearId = event.target.value;
                    const firstSemester = bookingPeriods.find(
                      (period) => period.year.id === yearId,
                    )?.semester;
                    selectBookingPeriod(yearId, firstSemester?.id || "");
                  }}
                  className="rounded-lg border bg-white px-3 py-2 text-sm"
                >
                  <option value="">Select academic year</option>
                  {[
                    ...new Map(
                      bookingPeriods.map((item) => [item.year.id, item.year]),
                    ).values(),
                  ].map((year) => (
                    <option key={year.id} value={year.id}>
                      {year.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Semester
                </span>
                <select
                  value={bookingPeriod.semesterId}
                  onChange={(event) =>
                    selectBookingPeriod(
                      bookingPeriod.academicYearId,
                      event.target.value,
                    )
                  }
                  disabled={!bookingPeriod.academicYearId}
                  className="rounded-lg border bg-white px-3 py-2 text-sm disabled:opacity-50"
                >
                  <option value="">Select semester</option>
                  {bookingPeriods
                    .filter(
                      (period) =>
                        period.year.id === bookingPeriod.academicYearId,
                    )
                    .map((period) => (
                      <option
                        key={period.semester.id}
                        value={period.semester.id}
                      >
                        {period.semester.name}
                      </option>
                    ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Opens on
                </span>
                <input
                  type="date"
                  value={bookingWindow.opens_on || ""}
                  onChange={(event) =>
                    setBookingWindow((current) => ({
                      ...current,
                      opens_on: event.target.value,
                    }))
                  }
                  disabled={!bookingWindow.booking_enabled}
                  className="rounded-lg border bg-white px-3 py-2 text-sm disabled:opacity-50"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Closes on
                </span>
                <input
                  type="date"
                  value={bookingWindow.closes_on || ""}
                  onChange={(event) =>
                    setBookingWindow((current) => ({
                      ...current,
                      closes_on: event.target.value,
                    }))
                  }
                  disabled={!bookingWindow.booking_enabled}
                  className="rounded-lg border bg-white px-3 py-2 text-sm disabled:opacity-50"
                />
              </label>
            </div>
            <label className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={bookingWindow.booking_enabled === true}
                onChange={(event) =>
                  setBookingWindow((current) => ({
                    ...current,
                    booking_enabled: event.target.checked,
                  }))
                }
                className="h-4 w-4 accent-primary"
              />
              Allow student hostel bookings for this period
            </label>
            <p className="mt-2 text-xs font-medium text-slate-600">
              Status:{" "}
              {String(bookingWindow.status || "unconfigured").replaceAll(
                "_",
                " ",
              )}
            </p>
          </form>
          <section className="rounded-xl border bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold">Accommodation overview</h3>
                <p className="text-sm text-slate-500">
                  Live capacity and booking status across all hostel blocks.
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {inventorySummary.beds} total beds
              </span>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <div className="rounded-lg border bg-slate-50 p-4">
                <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                  <span>Occupancy</span>
                  <span>{occupancyRate}%</span>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full rounded-full bg-indigo-600 transition-all"
                    style={{ width: `${occupancyRate}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {inventorySummary.allocated} allocated of{" "}
                  {inventorySummary.beds} beds
                </p>
              </div>
              <div className="rounded-lg border bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-700">
                  Capacity available
                </p>
                <p className="mt-2 text-2xl font-bold text-emerald-700">
                  {inventorySummary.available}
                </p>
                <p className="text-xs text-slate-500">
                  Beds ready for reservation
                </p>
              </div>
              <div className="rounded-lg border bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-700">
                  Payment activity
                </p>
                <p className="mt-2 text-2xl font-bold text-amber-700">
                  {inventorySummary.held}
                </p>
                <p className="text-xs text-slate-500">
                  Beds currently held pending payment
                </p>
              </div>
            </div>
          </section>
          {dashboard.excess_credits?.length > 0 && (
            <section className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold">
                    Excess accommodation payments
                  </h3>
                  <p className="text-sm text-slate-600">
                    Surpluses from cheaper room changes. Student credits are
                    already posted to the fee account; non-student claims stay
                    here for payout and clearance.
                  </p>
                </div>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
                  {
                    dashboard.excess_credits.filter(
                      (credit) =>
                        credit.status === "AVAILABLE" &&
                        ["APPROVED", "RESOLVED"].includes(
                          credit.request_status,
                        ),
                    ).length
                  }{" "}
                  available
                </span>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-emerald-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-2 py-2">Beneficiary</th>
                      <th className="px-2 py-2">Room change</th>
                      <th className="px-2 py-2">Credit</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-emerald-100">
                    {dashboard.excess_credits.map((credit) => (
                      <tr key={credit.id}>
                        <td className="px-2 py-3">
                          <p className="font-semibold text-slate-900">
                            {credit.student_name || "External beneficiary"}
                          </p>
                          <p className="text-xs text-slate-500">
                            {credit.admission_number || "No student number"}
                          </p>
                        </td>
                        <td className="px-2 py-3 text-xs text-slate-600">
                          {credit.target_block_name || "—"} · Room{" "}
                          {credit.target_room_number || "—"} · Bed{" "}
                          {credit.target_bed_number || "—"}
                        </td>
                        <td className="px-2 py-3 font-semibold text-emerald-700">
                          {money(credit.remaining_amount)}
                        </td>
                        <td className="px-2 py-3">
                          <span className="rounded-full border border-emerald-200 bg-white px-2 py-1 text-[11px] font-semibold text-emerald-700">
                            {credit.request_status === "APPROVAL_PENDING"
                              ? "PENDING APPROVAL"
                              : String(credit.status || "AVAILABLE").replaceAll(
                                  "_",
                                  " ",
                                )}
                          </span>
                        </td>
                        <td className="px-2 py-3 text-right">
                          {credit.status === "CLAIM_PENDING" ? (
                            <button
                              type="button"
                              disabled={busy === `credit-${credit.id}`}
                              onClick={() =>
                                run(
                                  `credit-${credit.id}`,
                                  () =>
                                    hostelService.processExcessCreditClaim(
                                      credit.id,
                                      "CLAIMED",
                                      `HOSTEL-${credit.id}`,
                                    ),
                                  "Excess payment marked as claimed and cleared",
                                )
                              }
                              className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                            >
                              {busy === `credit-${credit.id}`
                                ? "Saving…"
                                : "Mark paid"}
                            </button>
                          ) : credit.status === "AVAILABLE" &&
                            credit.student_id ? (
                            <span className="text-xs text-slate-500">
                              On fee account
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      {view === "inventory" && showInventoryForm && (
        <form
          onSubmit={submitInventory}
          className="rounded-xl border border-blue-100 bg-blue-50/60 p-5"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-slate-900">Create a hostel</h3>
              <p className="text-xs text-slate-500">
                One save creates the hostel, its rooms and every bed inside each
                room.
              </p>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
              Code generated automatically
            </span>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label="Hostel / block name">
              <input
                required
                placeholder="e.g. Kilimanjaro"
                value={form.name}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
                className="rounded-lg border px-3 py-2 text-sm"
              />
            </FormField>
            <FormField label="Hostel type">
              <select
                value={form.category}
                onChange={(event) => {
                  const category = event.target.value;
                  setForm({
                    ...form,
                    category,
                    studentBookingEnabled:
                      category === "staff" ? false : form.studentBookingEnabled,
                  });
                }}
                className="rounded-lg border bg-white px-3 py-2 text-sm"
              >
                <option value="standard">Standard</option>
                <option value="executive">Executive</option>
                <option value="premium">Premium</option>
                <option value="accessible">Accessible</option>
                <option value="staff">Staff</option>
              </select>
            </FormField>
            <FormField label="Gender eligibility">
              <select
                value={form.gender}
                onChange={(event) =>
                  setForm({ ...form, gender: event.target.value })
                }
                className="rounded-lg border bg-white px-3 py-2 text-sm"
              >
                <option value="any">Any gender</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
              </select>
            </FormField>
            <FormField label="Number of rooms">
              <input
                required
                type="number"
                min="1"
                max="500"
                placeholder="e.g. 12"
                value={form.roomCount}
                onChange={(event) =>
                  setForm({ ...form, roomCount: event.target.value })
                }
                className="rounded-lg border px-3 py-2 text-sm"
              />
            </FormField>
            <FormField label="Beds per room">
              <input
                required
                type="number"
                min="1"
                max="100"
                placeholder="e.g. 4"
                value={form.bedsPerRoom}
                onChange={(event) =>
                  setForm({ ...form, bedsPerRoom: event.target.value })
                }
                className="rounded-lg border px-3 py-2 text-sm"
              />
            </FormField>
            <FormField label="Rate per bed">
              <input
                required
                type="number"
                min="0"
                placeholder="KES per bed"
                value={form.bedRate}
                onChange={(event) =>
                  setForm({ ...form, bedRate: event.target.value })
                }
                className="rounded-lg border px-3 py-2 text-sm"
              />
            </FormField>
            <FormField label="Caution deposit">
              <input
                type="number"
                min="0"
                placeholder="KES"
                value={form.cautionDeposit}
                onChange={(event) =>
                  setForm({ ...form, cautionDeposit: event.target.value })
                }
                className="rounded-lg border px-3 py-2 text-sm"
              />
            </FormField>
            <FormField label="Utility fee">
              <input
                type="number"
                min="0"
                placeholder="KES"
                value={form.utilityFee}
                onChange={(event) =>
                  setForm({ ...form, utilityFee: event.target.value })
                }
                className="rounded-lg border px-3 py-2 text-sm"
              />
            </FormField>
            <div className="rounded-lg border bg-white px-3 py-2 text-sm sm:col-span-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Amenities
                </p>
                <button
                  type="button"
                  onClick={() =>
                    document
                      .getElementById("hostel-amenities-catalogue")
                      ?.scrollIntoView({ behavior: "smooth", block: "center" })
                  }
                  className="text-xs font-semibold text-indigo-700"
                >
                  Manage amenities
                </button>
              </div>
              <details className="relative">
                <summary className="cursor-pointer list-none rounded border px-3 py-2 text-slate-700">
                  {form.amenityIds.length
                    ? `${form.amenityIds.length} selected`
                    : "Choose amenities"}
                </summary>
                <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-lg border bg-white p-2 shadow-lg">
                  {amenities.filter((amenity) => amenity.active).length ? (
                    amenities
                      .filter((amenity) => amenity.active)
                      .map((amenity) => (
                        <label
                          key={amenity.id}
                          className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            checked={form.amenityIds.includes(amenity.id)}
                            onChange={(event) =>
                              setForm({
                                ...form,
                                amenityIds: event.target.checked
                                  ? [...form.amenityIds, amenity.id]
                                  : form.amenityIds.filter(
                                      (id) => id !== amenity.id,
                                    ),
                              })
                            }
                          />
                          {amenity.name}
                        </label>
                      ))
                  ) : (
                    <p className="p-2 text-xs text-slate-500">
                      Add amenities below first.
                    </p>
                  )}
                  <div className="mt-2 border-t pt-2 text-right">
                    <button
                      type="button"
                      onClick={(event) =>
                        event.currentTarget
                          .closest("details")
                          ?.removeAttribute("open")
                      }
                      className="rounded-md bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </details>
            </div>
            <label className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={
                  studentBookingForcedOff ? false : form.studentBookingEnabled
                }
                disabled={studentBookingForcedOff}
                onChange={(event) =>
                  setForm({
                    ...form,
                    studentBookingEnabled: event.target.checked,
                  })
                }
              />
              <span>
                Allow students to book this hostel
                <span className="ml-1 text-xs text-slate-500">
                  {studentBookingForcedOff
                    ? "(staff hostel — student access disabled)"
                    : "(can be enabled or disabled for premium/executive)"}
                </span>
              </span>
            </label>
          </div>
          <button
            disabled={busy === "inventory"}
            className="mt-4 rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy === "inventory" ? "Creating…" : "Create hostel"}
          </button>
        </form>
      )}

      {view === "amenities" && (
        <section
          id="hostel-amenities-catalogue"
          className="scroll-mt-24 rounded-xl border bg-white p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold">Amenity catalogue</h3>
              <p className="text-sm text-slate-500">
                Pre-create amenities once, then select several when creating a
                hostel.
              </p>
            </div>
            <form onSubmit={saveAmenity} className="flex items-end gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Amenity name
                </span>
                <input
                  required
                  value={amenityName}
                  onChange={(event) => setAmenityName(event.target.value)}
                  placeholder="e.g. Wi-Fi, Laundry"
                  className="w-48 rounded-lg border px-3 py-2 text-sm"
                />
              </label>
              <button
                disabled={busy === "amenity"}
                className="rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                {editingAmenity ? "Update" : "Add"}
              </button>
              {editingAmenity && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingAmenity(null);
                    setAmenityName("");
                  }}
                  className="rounded-lg border px-3 py-2 text-sm"
                >
                  Cancel
                </button>
              )}
            </form>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {amenities.length ? (
              amenities.map((amenity) => (
                <div
                  key={amenity.id}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm ${amenity.active ? "bg-slate-50" : "bg-slate-100 text-slate-400"}`}
                >
                  <span>{amenity.name}</span>
                  {!amenity.active && <span className="text-xs">removed</span>}
                  {amenity.active && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingAmenity(amenity);
                        setAmenityName(amenity.name);
                      }}
                      className="text-xs font-semibold text-indigo-700"
                    >
                      Edit
                    </button>
                  )}
                  {!amenity.active && (
                    <button
                      type="button"
                      disabled={busy === `amenity-${amenity.id}`}
                      onClick={() => restoreAmenity(amenity)}
                      className="text-xs font-semibold text-emerald-700 disabled:opacity-50"
                    >
                      Restore
                    </button>
                  )}
                  {amenity.active && (
                    <button
                      type="button"
                      disabled={busy === `amenity-${amenity.id}`}
                      onClick={() => removeAmenity(amenity)}
                      className="text-xs font-semibold text-rose-700 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">
                No amenities configured yet.
              </p>
            )}
          </div>
        </section>
      )}

      <div className="space-y-6">
        {view === "inventory" && (
          <section
            id="hostel-inventory"
            className="scroll-mt-24 rounded-xl border bg-white p-5 shadow-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold">Inventory</h3>
                <p className="text-sm text-slate-500">
                  Hostels, rooms and beds
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {blockOptions.length} blocks
              </span>
            </div>
            <div className="mt-4 grid gap-2 md:grid-cols-[minmax(0,1fr)_180px_180px]">
              <label className="relative block">
                <span className="sr-only">Search hostels</span>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={inventoryQuery}
                  onChange={(event) => setInventoryQuery(event.target.value)}
                  placeholder="Search hostel or code"
                  className="w-full rounded-lg border bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>
              <select
                aria-label="Filter by hostel type"
                value={inventoryCategory}
                onChange={(event) => setInventoryCategory(event.target.value)}
                className="rounded-lg border bg-white px-3 py-2 text-sm"
              >
                <option value="all">All types</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
                <option value="executive">Executive</option>
                <option value="accessible">Accessible</option>
                <option value="staff">Staff</option>
              </select>
              <select
                aria-label="Filter by occupancy"
                value={inventoryStatus}
                onChange={(event) => setInventoryStatus(event.target.value)}
                className="rounded-lg border bg-white px-3 py-2 text-sm"
              >
                <option value="all">All occupancy</option>
                <option value="available">Has available beds</option>
                <option value="held">Has held beds</option>
                <option value="allocated">Has allocated beds</option>
              </select>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1">
                <Filter className="h-3.5 w-3.5" /> {inventoryRows.length} of{" "}
                {blockOptions.length} hostels shown
              </span>
              {(inventoryQuery ||
                inventoryCategory !== "all" ||
                inventoryStatus !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setInventoryQuery("");
                    setInventoryCategory("all");
                    setInventoryStatus("all");
                  }}
                  className="font-semibold text-indigo-700 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
            <div className="mt-3 overflow-x-auto rounded-lg border">
              <div className="min-w-[860px]">
                <div className="grid grid-cols-[minmax(240px,1.4fr)_minmax(190px,1fr)_minmax(170px,1fr)_auto] gap-3 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <span>Hostel</span>
                  <span>Capacity / occupancy</span>
                  <span>Access / rate</span>
                  <span className="text-right">Actions</span>
                </div>
                <div className="divide-y">
                  {loading ? (
                    <p className="py-8 text-center text-sm text-slate-500">
                      Loading inventory…
                    </p>
                  ) : blockOptions.length === 0 ? (
                    <div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
                      No hostel inventory yet. Add the first hostel/block to
                      start the pool.
                    </div>
                  ) : inventoryRows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500">
                      No hostels match the selected filters.
                    </div>
                  ) : (
                    inventoryRows.map(({ block, counts }) => {
                      return (
                        <div
                          key={block.id}
                          className="grid grid-cols-[minmax(240px,1.4fr)_minmax(190px,1fr)_minmax(170px,1fr)_auto] items-center gap-3 px-3 py-3 transition hover:bg-slate-50"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-900">
                              {block.name}{" "}
                              <span className="font-mono text-xs text-slate-500">
                                ({block.code})
                              </span>
                            </p>
                            <p className="mt-0.5 truncate text-xs text-slate-500">
                              <span className="capitalize">
                                {block.hostel_category || "standard"}
                              </span>{" "}
                              · {block.gender_eligibility} ·{" "}
                              {block.amenities?.length || 0} amenities
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-700">
                              {block.rooms?.length || 0} rooms ·{" "}
                              {(block.rooms || []).reduce(
                                (total, room) =>
                                  total + (room.beds?.length || 0),
                                0,
                              )}{" "}
                              beds
                            </p>
                            <div className="mt-1 flex flex-wrap gap-1 text-[10px] font-semibold">
                              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                                {counts.available} available
                              </span>
                              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">
                                {counts.held} held
                              </span>
                              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">
                                {counts.allocated} allocated
                              </span>
                            </div>
                          </div>
                          <div className="text-xs text-slate-600">
                            <p className="font-semibold text-slate-800">
                              {block.hostel_category === "staff" ||
                              !block.student_booking_enabled
                                ? "Student booking closed"
                                : "Student booking open"}
                            </p>
                            <p className="mt-0.5">
                              Bed rate{" "}
                              {money(Number(block.bed_rate ?? block.room_rate))}
                            </p>
                            <button
                              type="button"
                              disabled={
                                block.hostel_category === "staff" ||
                                busy === `booking-${block.id}`
                              }
                              onClick={() =>
                                run(
                                  `booking-${block.id}`,
                                  () =>
                                    hostelService.setStudentBookingEnabled(
                                      block.id,
                                      !block.student_booking_enabled,
                                    ),
                                  "Student booking setting updated",
                                )
                              }
                              className="mt-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {block.hostel_category === "staff"
                                ? "Staff only"
                                : block.student_booking_enabled
                                  ? "Disable booking"
                                  : "Enable booking"}
                            </button>
                          </div>
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => setViewingBlock(block)}
                              className="inline-flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs font-semibold text-slate-700"
                            >
                              <Eye className="h-3.5 w-3.5" /> View
                            </button>
                            <button
                              type="button"
                              onClick={() => openBlockEditor(block)}
                              className="inline-flex items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs font-semibold text-indigo-700"
                            >
                              <Pencil className="h-3.5 w-3.5" /> Edit
                            </button>
                            <button
                              type="button"
                              disabled={busy === `delete-block-${block.id}`}
                              onClick={() => removeBlock(block)}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-xs font-semibold text-rose-700 disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {busy === `delete-block-${block.id}`
                                ? "Deleting…"
                                : "Delete"}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
        {view === "reservations" && (
          <section
            id="hostel-reservations"
            className="scroll-mt-24 rounded-xl border bg-white p-5 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Reservation queue</h3>
                <p className="text-sm text-slate-500">
                  Payment and allocation events are auditable.
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {dashboard.reservations?.length || 0} records
              </span>
            </div>
            <div className="mt-4 overflow-x-auto">
              {dashboard.reservations?.length ? (
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="border-b text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="pb-3 pr-3">Student</th>
                      <th className="pb-3 pr-3">Bed</th>
                      <th className="pb-3 pr-3">State</th>
                      <th className="pb-3 pr-3">Invoice</th>
                      <th className="pb-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {dashboard.reservations.map((item) => (
                      <tr key={item.id}>
                        <td className="py-3 pr-3">
                          <p className="font-semibold">{item.student_name}</p>
                          <p className="text-xs text-slate-500">
                            {item.admission_number || item.student_id}
                          </p>
                        </td>
                        <td className="py-3 pr-3">
                          {item.block_name} · {item.room_number} ·{" "}
                          {item.bed_number}
                          <p className="text-xs text-slate-500">
                            {item.hold_expires_at
                              ? `Hold until ${dateTime(item.hold_expires_at)}`
                              : dateTime(item.allocated_at)}
                          </p>
                        </td>
                        <td className="py-3 pr-3">
                          <span
                            className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${statusTone(item.status)}`}
                          >
                            {item.status.replaceAll("_", " ")}
                          </span>
                        </td>
                        <td className="py-3 pr-3">
                          {item.invoice ? (
                            <>
                              <p className="font-mono text-xs">
                                {item.invoice.invoice_number}
                              </p>
                              <p className="text-xs text-slate-500">
                                {money(item.invoice.amount)} ·{" "}
                                {item.invoice.status.replaceAll("_", " ")}
                              </p>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="py-3">
                          {[
                            "PENDING_PAYMENT",
                            "HELD_RESERVED",
                            "PAYMENT_REVIEW",
                          ].includes(item.status) && (
                            <div className="flex flex-wrap gap-2">
                              <button
                                disabled={busy === `pay-${item.id}`}
                                onClick={() =>
                                  run(
                                    `pay-${item.id}`,
                                    () =>
                                      hostelService.confirmPayment(
                                        item.id,
                                        `STAFF-${item.id}`,
                                      ),
                                    "Payment settled and bed allocated",
                                  )
                                }
                                className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
                              >
                                Settle payment
                              </button>
                              <button
                                disabled={busy === `cancel-${item.id}`}
                                onClick={() =>
                                  run(
                                    `cancel-${item.id}`,
                                    () =>
                                      hostelService.cancelReservation(item.id),
                                    "Reservation released",
                                  )
                                }
                                className="rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-700 disabled:opacity-50"
                              >
                                Release
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="rounded-lg border border-dashed p-10 text-center text-sm text-slate-500">
                  No reservations have been created.
                </div>
              )}
            </div>
          </section>
        )}
      </div>
      {viewingBlock && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/50 p-4"
          role="presentation"
          onClick={() => setViewingBlock(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="view-hostel-title"
            className="mx-auto my-4 max-h-[calc(100vh-2rem)] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:my-8"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                  Hostel details
                </p>
                <h3 id="view-hostel-title" className="mt-1 text-xl font-bold">
                  {viewingBlock.name}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {viewingBlock.code} ·{" "}
                  <span className="capitalize">
                    {viewingBlock.hostel_category || "standard"}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewingBlock(null)}
                className="rounded-lg border p-2 text-slate-500 hover:bg-slate-50"
                aria-label="Close hostel details"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Eligibility
                </p>
                <p className="mt-1 font-semibold capitalize">
                  {viewingBlock.gender_eligibility || "any"}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Rate per bed
                </p>
                <p className="mt-1 font-semibold">
                  {money(viewingBlock.bed_rate ?? viewingBlock.room_rate)}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Rooms / beds
                </p>
                <p className="mt-1 font-semibold">
                  {viewingBlock.rooms?.length || 0} /{" "}
                  {(viewingBlock.rooms || []).reduce(
                    (total, room) => total + (room.beds?.length || 0),
                    0,
                  )}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Student booking
                </p>
                <p className="mt-1 font-semibold">
                  {viewingBlock.hostel_category === "staff" ||
                  !viewingBlock.student_booking_enabled
                    ? "Closed"
                    : "Open"}
                </p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Fees
                </p>
                <p className="mt-1 text-sm text-slate-700">
                  Caution {money(viewingBlock.caution_deposit)} · Utilities{" "}
                  {money(viewingBlock.utility_fee)} per bed
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Amenities
                </p>
                <p className="mt-1 text-sm text-slate-700">
                  {viewingBlock.amenities?.length
                    ? viewingBlock.amenities.join(", ")
                    : "No amenities listed"}
                </p>
              </div>
            </div>
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Rooms and beds
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {(viewingBlock.rooms || []).map((room) => (
                  <div key={room.id} className="rounded-lg border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">
                        Room {room.room_number}
                      </span>
                      <span className="text-xs capitalize text-slate-500">
                        {room.room_type} · {room.capacity} beds
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(room.beds || []).map((bed) => {
                        const displayStatus = effectiveBedStatus(
                          bed,
                          dashboard.reservations,
                        );
                        return (
                          <span
                            key={bed.id}
                            className={
                              "rounded border px-2 py-1 text-[11px] font-semibold " +
                              statusTone(displayStatus)
                            }
                          >
                            {bed.bed_number} ·{" "}
                            {displayStatus.replaceAll("_", " ")}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {editingBlock && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-hidden bg-slate-950/50 p-2 sm:p-4"
          role="presentation"
          onClick={() => setEditingBlock(null)}
        >
          <form
            onSubmit={submitBlockEdit}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-hostel-title"
            className="mx-auto my-0 flex max-h-[calc(100dvh-1rem)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white p-4 shadow-2xl sm:my-2 sm:max-h-[calc(100dvh-2rem)] sm:p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                  Edit hostel
                </p>
                <h3 id="edit-hostel-title" className="mt-1 text-xl font-bold">
                  {editingBlock.name}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Update rates, eligibility, amenities and booking access.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingBlock(null)}
                className="rounded-lg border p-2 text-slate-500 hover:bg-slate-50"
                aria-label="Close edit hostel"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <FormField label="Hostel / block name">
                  <input
                    required
                    value={blockForm.name}
                    onChange={(event) =>
                      setBlockForm({ ...blockForm, name: event.target.value })
                    }
                    className="rounded-lg border px-3 py-2 text-sm"
                  />
                </FormField>
                <FormField label="Hostel type">
                  <select
                    value={blockForm.category}
                    onChange={(event) => {
                      const category = event.target.value;
                      setBlockForm({
                        ...blockForm,
                        category,
                        studentBookingEnabled:
                          category === "staff"
                            ? false
                            : blockForm.studentBookingEnabled,
                      });
                    }}
                    className="rounded-lg border bg-white px-3 py-2 text-sm"
                  >
                    <option value="standard">Standard</option>
                    <option value="executive">Executive</option>
                    <option value="premium">Premium</option>
                    <option value="accessible">Accessible</option>
                    <option value="staff">Staff</option>
                  </select>
                </FormField>
                <FormField label="Gender eligibility">
                  <select
                    value={blockForm.gender}
                    onChange={(event) =>
                      setBlockForm({ ...blockForm, gender: event.target.value })
                    }
                    className="rounded-lg border bg-white px-3 py-2 text-sm"
                  >
                    <option value="any">Any gender</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </FormField>
                <FormField label="Rate per bed">
                  <input
                    required
                    type="number"
                    min="0"
                    value={blockForm.bedRate}
                    onChange={(event) =>
                      setBlockForm({
                        ...blockForm,
                        bedRate: event.target.value,
                      })
                    }
                    className="rounded-lg border px-3 py-2 text-sm"
                  />
                </FormField>
                <FormField label="Caution deposit">
                  <input
                    type="number"
                    min="0"
                    value={blockForm.cautionDeposit}
                    onChange={(event) =>
                      setBlockForm({
                        ...blockForm,
                        cautionDeposit: event.target.value,
                      })
                    }
                    className="rounded-lg border px-3 py-2 text-sm"
                  />
                </FormField>
                <FormField label="Utility fee">
                  <input
                    type="number"
                    min="0"
                    value={blockForm.utilityFee}
                    onChange={(event) =>
                      setBlockForm({
                        ...blockForm,
                        utilityFee: event.target.value,
                      })
                    }
                    className="rounded-lg border px-3 py-2 text-sm"
                  />
                </FormField>
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Amenities
                    </p>
                    <span className="text-xs text-slate-500">
                      {blockForm.amenityIds.length} selected
                    </span>
                  </div>
                  <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                    {amenities.filter((amenity) => amenity.active).length ? (
                      amenities
                        .filter((amenity) => amenity.active)
                        .map((amenity) => (
                          <label
                            key={amenity.id}
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm hover:bg-white"
                          >
                            <input
                              type="checkbox"
                              checked={blockForm.amenityIds.includes(
                                amenity.id,
                              )}
                              onChange={(event) =>
                                setBlockForm({
                                  ...blockForm,
                                  amenityIds: event.target.checked
                                    ? [...blockForm.amenityIds, amenity.id]
                                    : blockForm.amenityIds.filter(
                                        (id) => id !== amenity.id,
                                      ),
                                })
                              }
                            />
                            {amenity.name}
                          </label>
                        ))
                    ) : (
                      <p className="text-sm text-slate-500">
                        No active amenities configured.
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <label className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={
                        blockForm.category === "staff"
                          ? false
                          : blockForm.studentBookingEnabled
                      }
                      disabled={blockForm.category === "staff"}
                      onChange={(event) =>
                        setBlockForm({
                          ...blockForm,
                          studentBookingEnabled: event.target.checked,
                        })
                      }
                    />
                    <span>
                      Allow students to book this hostel
                      {blockForm.category === "staff" && (
                        <span className="ml-1 text-xs text-slate-500">
                          (staff hostels are always closed to students)
                        </span>
                      )}
                    </span>
                  </label>
                  <section className="rounded-lg border bg-slate-50 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                          Inventory size
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          Set how many rooms and beds this hostel should
                          contain.
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-slate-500">
                        Current: {(editingBlock.rooms || []).length} rooms ·{" "}
                        {(editingBlock.rooms || []).reduce(
                          (total, room) => total + (room.beds || []).length,
                          0,
                        )}{" "}
                        beds
                      </span>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <FormField label="Number of rooms">
                        <input
                          required
                          type="number"
                          min="1"
                          max="500"
                          value={blockForm.roomCount}
                          onChange={(event) =>
                            setBlockForm({
                              ...blockForm,
                              roomCount: event.target.value,
                            })
                          }
                          className="rounded-lg border bg-white px-3 py-2 text-sm"
                        />
                      </FormField>
                      <FormField label="Beds per room">
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={blockForm.bedsPerRoom}
                          onChange={(event) =>
                            setBlockForm({
                              ...blockForm,
                              bedsPerRoom: event.target.value,
                            })
                          }
                          className="rounded-lg border bg-white px-3 py-2 text-sm"
                        />
                      </FormField>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowRoomBedEditor((value) => !value)}
                      className="mt-3 inline-flex items-center rounded-lg border bg-white px-3 py-2 text-xs font-semibold text-indigo-700"
                    >
                      {showRoomBedEditor
                        ? "Hide room and bed details"
                        : "Edit room and bed details"}
                    </button>
                  </section>
                </div>
              </div>
              {showRoomBedEditor && (
                <section className="mt-4 rounded-lg border">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-slate-50 px-3 py-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Rooms and beds
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Edit room details and maintain each generated bed.
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-slate-500">
                      {(editingBlock.rooms || []).length} rooms ·{" "}
                      {(editingBlock.rooms || []).reduce(
                        (total, room) => total + (room.beds || []).length,
                        0,
                      )}{" "}
                      beds
                    </span>
                  </div>
                  <div className="divide-y">
                    {(editingBlock.rooms || []).map((room) => {
                      const roomValues = roomForms[room.id] || {
                        roomNumber: room.room_number || "",
                        roomType: room.room_type || "shared",
                        capacity: room.capacity || room.beds?.length || 1,
                        gender: room.gender_eligibility || "any",
                      };
                      return (
                        <details key={room.id} className="group">
                          <summary className="cursor-pointer list-none px-3 py-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-semibold">
                                Room {roomValues.roomNumber}
                              </span>
                              <span className="text-xs capitalize text-slate-500">
                                {roomValues.roomType} · {room.beds?.length || 0}
                                /{roomValues.capacity} beds
                              </span>
                            </div>
                          </summary>
                          <div className="border-t bg-white p-3">
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                              <FormField label="Room number">
                                <input
                                  required
                                  value={roomValues.roomNumber}
                                  onChange={(event) =>
                                    setRoomForms((current) => ({
                                      ...current,
                                      [room.id]: {
                                        ...roomValues,
                                        roomNumber: event.target.value,
                                      },
                                    }))
                                  }
                                  className="rounded-lg border px-3 py-2 text-sm"
                                />
                              </FormField>
                              <FormField label="Room type">
                                <select
                                  value={roomValues.roomType}
                                  onChange={(event) =>
                                    setRoomForms((current) => ({
                                      ...current,
                                      [room.id]: {
                                        ...roomValues,
                                        roomType: event.target.value,
                                      },
                                    }))
                                  }
                                  className="rounded-lg border bg-white px-3 py-2 text-sm"
                                >
                                  <option value="single">Single</option>
                                  <option value="shared">Shared</option>
                                  <option value="double">Double</option>
                                  <option value="accessible">Accessible</option>
                                </select>
                              </FormField>
                              <FormField label="Bed capacity">
                                <input
                                  required
                                  type="number"
                                  min={room.beds?.length || 1}
                                  max="100"
                                  value={roomValues.capacity}
                                  onChange={(event) =>
                                    setRoomForms((current) => ({
                                      ...current,
                                      [room.id]: {
                                        ...roomValues,
                                        capacity: event.target.value,
                                      },
                                    }))
                                  }
                                  className="rounded-lg border px-3 py-2 text-sm"
                                />
                              </FormField>
                              <FormField label="Gender eligibility">
                                <select
                                  value={roomValues.gender}
                                  onChange={(event) =>
                                    setRoomForms((current) => ({
                                      ...current,
                                      [room.id]: {
                                        ...roomValues,
                                        gender: event.target.value,
                                      },
                                    }))
                                  }
                                  className="rounded-lg border bg-white px-3 py-2 text-sm"
                                >
                                  <option value="any">Any gender</option>
                                  <option value="female">Female</option>
                                  <option value="male">Male</option>
                                  <option value="other">Other</option>
                                </select>
                              </FormField>
                            </div>
                            <div className="mt-3 flex justify-end">
                              <button
                                type="button"
                                disabled={busy === "room-" + room.id}
                                onClick={() => saveRoomEdit(room)}
                                className="rounded-lg border px-3 py-2 text-xs font-semibold text-indigo-700 disabled:opacity-50"
                              >
                                {busy === "room-" + room.id
                                  ? "Saving room…"
                                  : "Save room"}
                              </button>
                            </div>
                            <div className="mt-4 rounded-lg border bg-slate-50 p-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                  Beds in room
                                </p>
                                <span className="text-xs text-slate-500">
                                  {room.beds?.length || 0} generated
                                </span>
                              </div>
                              <div className="mt-2 space-y-3">
                                {(room.beds || []).map((bed) => {
                                  const bedValues = bedForms[bed.id] || {
                                    bedNumber: bed.bed_number || "",
                                    accessible: Boolean(
                                      bed.special_needs_accessible,
                                    ),
                                    notes: bed.notes || "",
                                    status: bed.status || "AVAILABLE",
                                  };
                                  const statusLocked = [
                                    "HELD_RESERVED",
                                    "ALLOCATED",
                                    "OCCUPIED",
                                  ].includes(bed.status);
                                  return (
                                    <div
                                      key={bed.id}
                                      className="rounded-lg border bg-white p-3"
                                    >
                                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                        <FormField label="Bed number">
                                          <input
                                            required
                                            value={bedValues.bedNumber}
                                            onChange={(event) =>
                                              setBedForms((current) => ({
                                                ...current,
                                                [bed.id]: {
                                                  ...bedValues,
                                                  bedNumber: event.target.value,
                                                },
                                              }))
                                            }
                                            className="rounded-lg border px-3 py-2 text-sm"
                                          />
                                        </FormField>
                                        <FormField label="Bed status">
                                          <select
                                            value={bedValues.status}
                                            disabled={statusLocked}
                                            onChange={(event) =>
                                              setBedForms((current) => ({
                                                ...current,
                                                [bed.id]: {
                                                  ...bedValues,
                                                  status: event.target.value,
                                                },
                                              }))
                                            }
                                            className="rounded-lg border bg-white px-3 py-2 text-sm disabled:bg-slate-100"
                                          >
                                            <option value="AVAILABLE">
                                              Available
                                            </option>
                                            <option value="MAINTENANCE">
                                              Maintenance
                                            </option>
                                            <option value="OUT_OF_SERVICE">
                                              Out of service
                                            </option>
                                            <option value="HELD_RESERVED">
                                              Held / reserved
                                            </option>
                                            <option value="ALLOCATED">
                                              Allocated
                                            </option>
                                            <option value="OCCUPIED">
                                              Occupied
                                            </option>
                                          </select>
                                        </FormField>
                                        <FormField label="Notes">
                                          <textarea
                                            rows="1"
                                            value={bedValues.notes}
                                            onChange={(event) =>
                                              setBedForms((current) => ({
                                                ...current,
                                                [bed.id]: {
                                                  ...bedValues,
                                                  notes: event.target.value,
                                                },
                                              }))
                                            }
                                            placeholder="Optional notes"
                                            className="resize-y rounded-lg border px-3 py-2 text-sm"
                                          />
                                        </FormField>
                                        <label className="flex items-center gap-2 self-end rounded-lg border px-3 py-2 text-sm">
                                          <input
                                            type="checkbox"
                                            checked={bedValues.accessible}
                                            onChange={(event) =>
                                              setBedForms((current) => ({
                                                ...current,
                                                [bed.id]: {
                                                  ...bedValues,
                                                  accessible:
                                                    event.target.checked,
                                                },
                                              }))
                                            }
                                          />
                                          Accessible bed
                                        </label>
                                      </div>
                                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                                        <span className="text-xs text-slate-500">
                                          {statusLocked
                                            ? "Status locked while this bed has an active reservation."
                                            : "Status can be changed for maintenance or availability."}
                                        </span>
                                        <button
                                          type="button"
                                          disabled={busy === "bed-" + bed.id}
                                          onClick={() =>
                                            saveBedEdit(bed, room.id)
                                          }
                                          className="rounded-lg border px-3 py-2 text-xs font-semibold text-indigo-700 disabled:opacity-50"
                                        >
                                          {busy === "bed-" + bed.id
                                            ? "Saving bed…"
                                            : "Save bed"}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </details>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
            <div className="mt-4 flex shrink-0 justify-end gap-2 border-t bg-white pt-3">
              <button
                type="button"
                onClick={() => setEditingBlock(null)}
                className="rounded-lg border px-4 py-2 text-sm font-semibold text-slate-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy === "edit-block-" + editingBlock.id}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                {busy === "edit-block-" + editingBlock.id
                  ? "Saving…"
                  : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
};

const HostelRequestQueue = ({ notify }) => {
  const [requests, setRequests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setRequests(await hostelService.getStaffRequests());
    } catch (error) {
      notify(
        error?.message || "Accommodation requests could not be loaded",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [notify]);
  React.useEffect(() => {
    load();
  }, [load]);
  const update = async (request, status) => {
    setBusy(request.id);
    try {
      await hostelService.updateRequestStatus(request.id, status);
      notify(
        `Request ${request.request_number || ""} marked ${status.toLowerCase().replaceAll("_", " ")}`,
      );
      await load();
    } catch (error) {
      notify(error?.message || "Request status could not be updated", "error");
    } finally {
      setBusy("");
    }
  };
  return (
    <section
      id="hostel-requests"
      className="scroll-mt-24 rounded-xl border bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold">Accommodation requests</h3>
          <p className="text-sm text-slate-500">
            Maintenance, room changes and welfare requests from students.
          </p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
          {requests.length} requests
        </span>
      </div>
      {loading ? (
        <p className="py-8 text-center text-sm text-slate-500">
          Loading requests…
        </p>
      ) : requests.length ? (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {requests.map((request) => (
            <div key={request.id} className="rounded-lg border p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{request.subject}</p>
                  <p className="text-xs text-slate-500">
                    {request.student_name} ·{" "}
                    {request.admission_number || "No admission number"}
                  </p>
                  <p className="text-xs capitalize text-slate-500">
                    {request.request_type.replaceAll("_", " ")} ·{" "}
                    {dateTime(request.created_at)}
                  </p>
                </div>
                <span
                  className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${request.status === "APPROVED" || request.status === "RESOLVED" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : request.status === "REJECTED" || request.status === "CANCELLED" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}
                >
                  {request.status.replaceAll("_", " ")}
                </span>
              </div>
              <p className="mt-3 text-sm text-slate-600">
                {request.description}
              </p>
              {request.block_name && (
                <p className="mt-2 text-xs text-slate-500">
                  Current: {request.block_name} · Room {request.room_number} ·
                  Bed {request.bed_number}
                </p>
              )}
              {request.request_type === "room_change" && (
                <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50/60 p-3 text-xs text-slate-700">
                  <p className="font-semibold text-blue-900">
                    Target: {request.target_block_name} · Room{" "}
                    {request.target_room_number} · Bed{" "}
                    {request.target_bed_number}
                  </p>
                  <p className="mt-1">
                    Extra charge: {money(request.difference_amount)} ·{" "}
                    {request.payment_status === "SETTLED"
                      ? "Paid"
                      : request.payment_status === "NOT_REQUIRED"
                        ? "No payment required"
                        : request.payment_status === "REFUNDED"
                          ? "Refunded"
                          : "Awaiting payment"}
                  </p>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {request.request_type === "room_change" &&
                  request.status === "APPROVAL_PENDING" && (
                    <>
                      <button
                        disabled={busy === request.id}
                        onClick={() => update(request, "APPROVED")}
                        className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        Approve transfer
                      </button>
                      <button
                        disabled={busy === request.id}
                        onClick={() => update(request, "REJECTED")}
                        className="rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-700 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </>
                  )}
                {request.request_type === "room_change" &&
                  request.status === "AWAITING_PAYMENT" && (
                    <button
                      disabled={busy === request.id}
                      onClick={() => update(request, "REJECTED")}
                      className="rounded-md border border-rose-200 px-2 py-1 text-xs font-semibold text-rose-700 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  )}
                {request.request_type !== "room_change" &&
                  ["OPEN", "IN_PROGRESS"].includes(request.status) && (
                    <>
                      <button
                        disabled={busy === request.id}
                        onClick={() => update(request, "IN_PROGRESS")}
                        className="rounded-md border px-2 py-1 text-xs font-semibold text-slate-700"
                      >
                        In progress
                      </button>
                      <button
                        disabled={busy === request.id}
                        onClick={() => update(request, "RESOLVED")}
                        className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white"
                      >
                        Resolve
                      </button>
                    </>
                  )}
              </div>
              {request.staff_note && (
                <p className="mt-2 rounded bg-slate-50 p-2 text-xs text-slate-600">
                  <strong>Staff note:</strong> {request.staff_note}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-dashed p-10 text-center text-sm text-slate-500">
          No student accommodation requests.
        </div>
      )}
    </section>
  );
};

const NonTeachingStaffDashboard = () => {
  const { user, profile, signOut } = useAuth();
  const { institutionSettings: institution } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const initialParams = new URLSearchParams(location.search);
  const actualRoles = [
    profile?.primary_role,
    ...(Array.isArray(profile?.secondary_roles) ? profile.secondary_roles : []),
  ]
    .map(normalizeRole)
    .filter(Boolean);
  const requestedViewAsRole = normalizeRole(initialParams.get("viewAs"));
  const previewableRoles = new Set([
    "hostel_officer",
    "transport_officer",
    "estates_officer",
  ]);
  const isRolePreview =
    actualRoles.includes("system_administrator") &&
    previewableRoles.has(requestedViewAsRole);
  const roles = isRolePreview ? [requestedViewAsRole] : actualRoles;
  const isSystemAdministrator = roles.includes("system_administrator");
  const implementedSystemWorkspaces = new Set(["hostel", "transport", "estates"]);
  const availableWorkspaces = workspaces.filter(
    (workspace) =>
      (isSystemAdministrator
        ? implementedSystemWorkspaces.has(workspace.id)
        : workspace.roles.some((role) => roles.includes(role))),
  );
  const requestedWorkspace = initialParams.get("workspace");
  const requestedView = initialParams.get("view");
  const initialWorkspace = availableWorkspaces.some(
    (workspace) => workspace.id === requestedWorkspace,
  )
    ? requestedWorkspace
    : defaultWorkspaceForRoles(roles) || availableWorkspaces[0]?.id || "hostel";
  const isTransportManager = roles.some((role) =>
    [
      "transport_officer",
      "system_administrator",
      "deputy_principal_administration",
    ].includes(role),
  );
  const [active, setActive] = React.useState(initialWorkspace);
  const [hostelView, setHostelView] = React.useState(
    initialWorkspace === "hostel" &&
      hostelSections.some((section) => section.id === requestedView)
      ? requestedView
      : "overview",
  );
  const [transportView, setTransportView] = React.useState(
    initialWorkspace === "transport" &&
      transportSections.some((section) => section.id === requestedView)
      ? requestedView
      : isTransportManager
        ? "overview"
        : "requests",
  );
  const [estatesView, setEstatesView] = React.useState(
    initialWorkspace === "estates" &&
      estatesSections.some((section) => section.id === requestedView)
      ? requestedView
      : "overview",
  );
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [showResourceBooking, setShowResourceBooking] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const notify = React.useCallback((message, type = "success") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3500);
  }, []);
  const roleKey = roles.join("|");
  const viewAsQuery = isRolePreview
    ? `&viewAs=${encodeURIComponent(requestedViewAsRole)}`
    : "";
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedWorkspace = params.get("workspace");
    const requestedView = params.get("view");
    const workspaceIsAvailable = availableWorkspaces.some(
      (workspace) => workspace.id === requestedWorkspace,
    );

    if (workspaceIsAvailable) {
      setActive(requestedWorkspace);
      if (requestedWorkspace === "transport") {
        setTransportView(
          transportSections.some((section) => section.id === requestedView)
            ? requestedView
            : isTransportManager
              ? "overview"
              : "requests",
        );
      }
      if (requestedWorkspace === "estates") {
        setEstatesView(
          estatesSections.some((section) => section.id === requestedView)
            ? requestedView
            : "overview",
        );
      }
      return;
    }

    if (!availableWorkspaces.some((workspace) => workspace.id === active)) {
      const fallbackWorkspace =
        defaultWorkspaceForRoles(roles) ||
        availableWorkspaces[0]?.id ||
        "hostel";
      setActive(fallbackWorkspace);
      if (fallbackWorkspace === "transport") {
        setTransportView(isTransportManager ? "overview" : "requests");
      }
      if (fallbackWorkspace === "estates") {
        setEstatesView("overview");
      }
    }
  }, [active, isTransportManager, location.search, roleKey]);
  const staffName =
    profile?.full_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    "Operations staff";
  const initials = getInstitutionInitials(institution);
  const logout = async () => {
    await signOut();
    navigate("/login");
  };
  const currentWorkspace = availableWorkspaces.find(
    (workspace) => workspace.id === active,
  );
  const workspaceNavigation = isSystemAdministrator
    ? availableWorkspaces.filter((workspace) => workspace.id === active)
    : availableWorkspaces;
  const visibleTransportSections = isTransportManager
    ? transportSections
    : transportSections.filter((section) => section.id === "requests");
  const openWorkspace = (workspaceId) => {
    setShowResourceBooking(false);
    const nextTransportView = isTransportManager ? "overview" : "requests";
    setActive(workspaceId);
    if (workspaceId === "hostel") {
      setHostelView("overview");
      navigate(`${location.pathname}?workspace=hostel&view=overview${viewAsQuery}`, {
        replace: true,
      });
    } else if (workspaceId === "transport") {
      setTransportView(nextTransportView);
      navigate(
        `${location.pathname}?workspace=transport&view=${nextTransportView}${viewAsQuery}`,
        { replace: true },
      );
    } else if (workspaceId === "estates") {
      setEstatesView("overview");
      navigate(`${location.pathname}?workspace=estates&view=overview${viewAsQuery}`, {
        replace: true,
      });
    } else {
      navigate(location.pathname, { replace: true });
    }
    setMobileOpen(false);
  };
  const openTransportView = (nextView) => {
    setTransportView(nextView);
    navigate(`${location.pathname}?workspace=transport&view=${nextView}${viewAsQuery}`, {
      replace: true,
    });
  };
  const openEstatesView = (nextView) => {
    setEstatesView(nextView);
    navigate(`${location.pathname}?workspace=estates&view=${nextView}${viewAsQuery}`, {
      replace: true,
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-[100] bg-primary text-primary-foreground shadow-lg">
        <div className="mx-auto flex min-h-16 max-w-[1600px] items-center gap-4 px-5">
          <button
            onClick={() => navigate("/")}
            className="flex shrink-0 items-center gap-3"
            aria-label="Portal home"
          >
            {institution?.logo_url ? (
              <img
                src={institution.logo_url}
                alt="Institution logo"
                className="h-10 w-10 rounded bg-white object-contain p-1"
              />
            ) : (
              <span className="grid h-10 w-10 place-items-center rounded bg-white/15">
                <Building2 className="h-5 w-5" />
              </span>
            )}
            <span className="font-bold tracking-[0.18em]">{initials}</span>
          </button>
          <nav className="ml-auto hidden min-w-0 flex-1 items-center justify-center gap-2 overflow-x-auto lg:flex">
            {workspaceNavigation.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => openWorkspace(workspace.id)}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${active === workspace.id ? "bg-white text-primary" : "hover:bg-white/10"}`}
              >
                <workspace.icon className="h-4 w-4" />
                {workspace.label}
              </button>
            ))}
            {active === "hostel" && (
              <>
                <span
                  className="mx-1 h-6 w-px shrink-0 bg-white/20"
                  aria-hidden="true"
                />
                {hostelSections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setHostelView(section.id)}
                    className={`shrink-0 rounded-lg px-2 py-2 text-xs font-semibold transition ${hostelView === section.id ? "bg-white/15" : "hover:bg-white/10"}`}
                  >
                    {section.label}
                  </button>
                ))}
              </>
            )}
            {active === "transport" && (
              <>
                <span
                  className="mx-1 h-6 w-px shrink-0 bg-white/20"
                  aria-hidden="true"
                />
                {visibleTransportSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => openTransportView(section.id)}
                      className={`shrink-0 rounded-lg px-2 py-2 text-xs font-semibold transition ${transportView === section.id ? "bg-white/15" : "hover:bg-white/10"}`}
                    >
                      <Icon className="mr-1 inline h-3.5 w-3.5" />
                      {section.label}
                    </button>
                  );
                })}
              </>
            )}
            {active === "estates" && (
              <>
                <span
                  className="mx-1 h-6 w-px shrink-0 bg-white/20"
                  aria-hidden="true"
                />
                {estatesSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => openEstatesView(section.id)}
                      className={`shrink-0 rounded-lg px-2 py-2 text-xs font-semibold transition ${estatesView === section.id ? "bg-white/15" : "hover:bg-white/10"}`}
                    >
                      <Icon className="mr-1 inline h-3.5 w-3.5" />
                      {section.label}
                    </button>
                  );
                })}
              </>
            )}
          </nav>
          <button
            className="ml-auto lg:hidden"
            onClick={() => setMobileOpen((value) => !value)}
          >
            {mobileOpen ? <X /> : <Menu />}
          </button>
          <div className="hidden items-center gap-3 lg:flex">
            <div className="text-right">
              <p className="max-w-44 truncate text-sm font-semibold">
                {staffName}
              </p>
              <p className="text-xs text-primary-foreground/70">
                {labelRole(
                  isRolePreview ? requestedViewAsRole : profile?.primary_role,
                )}
              </p>
            </div>
            <button
              onClick={logout}
              title="Sign out"
              className="rounded-lg p-2 hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        {mobileOpen && (
          <nav className="border-t border-white/10 bg-white px-5 py-2 text-slate-800 lg:hidden">
            {workspaceNavigation.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => openWorkspace(workspace.id)}
                className={`flex w-full items-center gap-2 rounded px-3 py-3 text-left text-sm font-semibold ${active === workspace.id ? "bg-blue-50 text-blue-700" : ""}`}
              >
                <workspace.icon className="h-4 w-4" />
                {workspace.label}
              </button>
            ))}
            {active === "hostel" && (
              <div className="mt-2 border-t border-slate-200 pt-2">
                <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Hostel sections
                </p>
                {hostelSections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => {
                      setHostelView(section.id);
                      setMobileOpen(false);
                    }}
                    className={`flex w-full items-center rounded px-3 py-2 text-left text-sm font-semibold ${hostelView === section.id ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-blue-50 hover:text-blue-700"}`}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            )}
            {active === "transport" && (
              <div className="mt-2 border-t border-slate-200 pt-2">
                <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Transport sections
                </p>
                {visibleTransportSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => {
                        openTransportView(section.id);
                        setMobileOpen(false);
                      }}
                      className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-semibold ${transportView === section.id ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-blue-50 hover:text-blue-700"}`}
                    >
                      <Icon className="h-4 w-4" />
                      {section.label}
                    </button>
                  );
                })}
              </div>
            )}
            {active === "estates" && (
              <div className="mt-2 border-t border-slate-200 pt-2">
                <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Estates sections
                </p>
                {estatesSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => {
                        openEstatesView(section.id);
                        setMobileOpen(false);
                      }}
                      className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm font-semibold ${estatesView === section.id ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-blue-50 hover:text-blue-700"}`}
                    >
                      <Icon className="h-4 w-4" />
                      {section.label}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setShowResourceBooking(true);
                setMobileOpen(false);
              }}
              className="flex w-full items-center gap-2 border-t py-3 text-left text-sm font-semibold text-blue-700"
            >
              <CalendarDays className="h-4 w-4" />
              Book a space
            </button>
            <button
              onClick={logout}
              className="flex w-full items-center gap-2 border-t py-3 text-left text-sm text-rose-700"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </nav>
        )}
      </header>
      <main className="mx-auto max-w-[1600px] space-y-6 px-5 py-7">
        {showResourceBooking ? (
          <ResourceServiceBooking
            embedded
            onBack={() => setShowResourceBooking(false)}
          />
        ) : <>
        {active === "hostel" ? (
          <>
            <HostelWorkspace notify={notify} view={hostelView} />
            {hostelView === "requests" && (
              <HostelRequestQueue notify={notify} />
            )}
          </>
        ) : active === "transport" ? (
          <>
            {roles.includes("transport_officer") && (
              <EstatesServiceRequests serviceDepartment="transport" notify={notify} />
            )}
            <TransportWorkspace
              notify={notify}
              view={transportView}
              manager={isTransportManager}
              onViewChange={openTransportView}
            />
          </>
        ) : active === "estates" ? (
          <EstatesWorkspace
            notify={notify}
            view={estatesView}
            onViewChange={openEstatesView}
          />
        ) : ["catering", "ict", "corporate"].includes(active) ? (
          <EstatesServiceRequests
            serviceDepartment={active === "catering" ? "catering" : active}
            notify={notify}
          />
        ) : (
          <section className="rounded-xl border bg-white p-10 text-center shadow-sm">
            <currentWorkspace.icon className="mx-auto h-12 w-12 text-blue-700" />
            <h2 className="mt-4 text-xl font-bold">
              {currentWorkspace.label} workspace
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-sm text-slate-500">
              This operational workspace is ready for its inventory and request
              workflows. Hostel ORRA is the first fully connected service.
            </p>
          </section>
        )}
        </>}
      </main>
      {toast && (
        <div
          className={`fixed bottom-5 right-5 z-[200] max-w-md rounded-lg border px-4 py-3 text-sm font-medium shadow-xl ${toast.type === "error" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default NonTeachingStaffDashboard;
